import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';

import { Stepper, Step, StepLabel } from '@mui/material';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';

import useUserStore from '../../store/useUserStore';
import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';

import axiosInstance from '../../config/axiosConfig';

import StepOne from './Step1';
import StepTwo from './Step2';
import StepThree from './Step3';
import StepFour from './Step4';

const CompanyCreate = () => {
    const location = useLocation();

    const companies = useUserStore((state) => state.companies);
    const clearCompanies = useUserStore((state) => state.clearCompanies)

    const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);
    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const [activeIndex, setActiveIndex] = useState(0);

    const [loading, setLoading] = useState(false);

    const [isValid, setIsValid] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [joinLoading, setJoinLoading] = useState(false);
    const [choice, setChoice] = useState('');

    const [invitationCode, setInvitationCode] = useState('');

    // Master object storing all steps
    const [bigObject, setBigObject] = useState({});

    // Step1
    const [basicInfosObject, setBasicInfosObject] = useState(bigObject?.basicInfosObject || {});

    // Step2
    const [contactInfosObject, setContactInfosObject] = useState(bigObject?.contactInfosObject || {});

    // Step3
    const [otherDetailsObject, setOtherDetailsObject] = useState(bigObject?.otherDetailsObject || {});

    const toast = useRef(null);

    const items = [
        { label: 'Informations basiques' },
        { label: 'Informations de contact' },
        { label: 'Détails additionnels' },
        { label: 'Récapitulatif' }
    ];

    const queryParams = new URLSearchParams(location.search);
    const urlInvitationCode = queryParams.get('invitation_code');

    const handleBasicInfosChange = (object) => {
        if (object.step2Data) {
            setContactInfosObject(object.step2Data);
        }
        if (object.step3Data) {
            setOtherDetailsObject(object.step3Data);
        }
        const { step2Data, step3Data, ...justStep1 } = object;
        setBasicInfosObject(justStep1);
    };

    const handleContactInfosChange = (object) => {
        setContactInfosObject(object);
    };

    const handleOtherDetailsChange = (object) => {
        setOtherDetailsObject(object);
    };

    useEffect(() => {
        setBigObject({
            basicInfosObject,
            contactInfosObject,
            otherDetailsObject
        });
    }, [basicInfosObject, contactInfosObject, otherDetailsObject]);

    const handleIsValid = (validObjOrFalse) => {
        if (!validObjOrFalse) return setIsValid(false);
        if (validObjOrFalse?.step === String(activeIndex)) return setIsValid(true);
    };

    const stepsComponents = [
        <StepOne handleChange={handleBasicInfosChange} inputText={basicInfosObject} isValid={handleIsValid} />,
        <StepTwo handleChange={handleContactInfosChange} contactInfosObject={contactInfosObject} isValid={handleIsValid} />,
        <StepThree handleChange={handleOtherDetailsChange} otherDetailsObject={otherDetailsObject} isValid={handleIsValid} />,
        <StepFour data={bigObject} />
    ];

    const handleSubmit = () => {
        setSubmitLoading(true);

        const companyData = {
            ...basicInfosObject,
            ...contactInfosObject,
            ...otherDetailsObject
        };

        axiosInstance.post('/dashboard/companies', companyData).then(response => {
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Entreprise créée avec succès', life: 10000 });
            setSubmitLoading(false);

            // This forces a refresh
            clearCompanies();

            setTimeout(() => {
                window.location.href = "/dashboard/company/create";
            }, 1000);

        }).catch(error => {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la création de l\'entreprise, veuillez vérifier le formulaire ou réessayer plus tard', life: 10000 });
            setSubmitLoading(false);
        });
    };

    const handleJoin = () => {
        if (invitationCode.length !== 8) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Le code d\'invitation doit comporter 8 caractères.', life: 10000 });
            return;
        }

        setJoinLoading(true);

        axiosInstance.post(`/dashboard/companies/invitations/${invitationCode}/accept`).then(response => {
            toast.current.show({ severity: 'success', summary: 'Succès', detail: `Vous avez rejoint avec succès: ${response.data.company_name}`, life: 10000 });

            // This forces a refresh
            clearCompanies();

            setTimeout(() => {
                window.location.href = "/dashboard/company/create";
            }, 1000);

        }).catch(error => {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur, votre code a peut-être expiré.', life: 10000 });
        }).finally(() => {
            setJoinLoading(false);
        });
    };

    const [filteredCompanies, setFilteredCompanies] = useState([...companies]);

    useEffect(() => {
        setFilteredCompanies([...companies]);
    }, [companies]);


    const onSearch = (event) => {
        const searchTerm = event.target.value.trim().toLowerCase();

        if (!searchTerm.length) {
            setFilteredCompanies([...companies]);
        } else {
            setLoading(true);

            setTimeout(() => {
                const result = companies.filter((company) =>
                    company.company.name.toLowerCase().includes(searchTerm)
                );
                setFilteredCompanies(result);
                setLoading(false);
            }, 500);
        }
    };


    const renderSkeleton = () => {
        return (
            <div className="company-grid">
                {[...Array(5)].map((_, index) => (
                    <div key={index} className="company-grid-item is-flex is-align-items-center">
                        <Skeleton shape="circle" size="3rem" />
                        <Skeleton width="10rem" className="ml-3" />
                    </div>
                ))}
            </div>
        );
    };

    const renderCompanies = () => {
        if (loading) {
            return renderSkeleton();
        }

        if (filteredCompanies.length === 0) {
            return <p className="m-2">Aucune entreprise correspondante trouvée.</p>;
        }

        return (
            <div className="company-grid">
                {filteredCompanies.map((company) => (
                    <div
                        key={company.company_id}
                        className="company-grid-item is-flex is-align-items-center"
                        onClick={() => fetchSelectedCompany(company?.company_id)}
                    >
                        <Avatar
                            image={
                                company.company.profile_picture
                                    ? `/dashboard/company/avatar/${company.company.profile_picture}`
                                    : '/images/dashboard/company-logo-placeholder.png'
                            }
                            label={`${company.company.name.slice(0, 2)}`}
                            size="large"
                            shape="circle"
                        />
                        <span className="ml-3">{company.company.name}</span>
                    </div>
                ))}
            </div>
        );
    };


    useEffect(() => {
        if (!urlInvitationCode) return;
        setChoice('join');
        setInvitationCode(urlInvitationCode);
        // handleJoin();
    }, [urlInvitationCode]);

    const fetchSelectedCompany = (currentCompanyId) => {
        if (currentCompanyId === selectedCompany?.company_id) return;
        axiosInstance.get(`/dashboard/companies/${currentCompanyId}`)
            .then(response => {
                const refresh_data = selectedCompany?.company_id !== response?.data?.company_id;
                setSelectedCompany(response.data);
                if (refresh_data) {
                    window.location.href = '/dashboard/';
                    window.location.reload();
                }
            }).catch(error => {
                console.error('Error fetching company data:', error);
            });
    };

    const renderChoice = () => {
        if (choice === '') {
            return (
                <div className='m-auto' style={{ maxWidth: 1000 }}>
                    <div className="box has-text-centered radius-20">
                        {companies.length > 0 && !selectedCompany && (
                            <p className="m-2">
                                Veuillez choisir une entreprise dans la liste ci-dessous, ou sélectionnez une option pour créer ou rejoindre une entreprise.
                            </p>
                        )}

                        <div className={clsx("columns is-justify-content-center", { 'mt-4': companies.length > 0 && !selectedCompany })}>
                            <div className="column is-half">
                                <div
                                    className="box company-choice-box p-5 is-clickable radius-8 borderSpin"
                                    onClick={() => setChoice('create')}
                                    style={{ border: '3px solid #4786C6' }}
                                >
                                    <h3 className="title is-4">Créer une Entreprise</h3>
                                    <p>Commencez votre propre entreprise en quelques étapes simples.</p>
                                </div>
                            </div>
                            <div className="column is-half">
                                <div
                                    className="box company-choice-box light-bacgkround p-5 is-clickable radius-8"
                                    onClick={() => setChoice('join')}
                                >
                                    <h3 className="title is-4">Rejoindre une Entreprise</h3>
                                    <p>Trouvez et rejoignez une entreprise existante avec une invitation.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {companies.length > 0 && !selectedCompany && (
                        <div className="box radius-20 mt-4">
                            {companies.length > 10 && (
                                <div className="field mb-4">
                                    <p className="control has-icons-left">
                                        <input
                                            className="input"
                                            type="text"
                                            placeholder="Rechercher une entreprise..."
                                            onChange={onSearch}
                                        />
                                        <span className="icon is-small is-left">
                                            <i className="fa-solid fa-magnifying-glass"></i>
                                        </span>
                                    </p>
                                </div>
                            )}

                            {renderCompanies()}
                        </div>
                    )}
                </div>
            );
        }

        if (choice === 'join') {
            return (
                <>
                    <Toast ref={toast} position="bottom-right" />

                    <div className="box has-text-centered radius-15" style={{ maxWidth: '500px', margin: '10vh auto' }}>
                        <h1 className='title deepsy-color mb-0'>Rejoindre une Entreprise</h1>
                        <p style={{ fontSize: '12px' }} className='mb-5 mt-0'>Veuillez entrer un code à 8 caractères fourni par votre entreprise</p>

                        <div className="has-text-left" style={{ margin: '0 auto', maxWidth: 300 }}>
                            <label className="label" htmlFor="invitationCode">Code d'invitation</label>
                            <input
                                className="input"
                                id="invitationCode"
                                value={invitationCode}
                                onChange={(e) => setInvitationCode(e.target.value)}
                            />
                        </div>

                        <div className="columns mt-3">
                            <div className="column has-text-left">
                                <button className="button m-4" disabled={joinLoading} onClick={() => { if (!joinLoading) setChoice('') }}>Retour</button>
                            </div>

                            <div className="column has-text-right">
                                <button
                                    className={clsx("button is-info is-outlined m-4", { 'is-loading': joinLoading })}
                                    onClick={handleJoin}
                                >
                                    Rejoindre
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        // WIZARD STEPS
        const accept = () => {
            window.location.reload();
        };

        const handleBackButton = (event) => {
            if (activeIndex === 0) {
                confirmPopup({
                    target: event.currentTarget,
                    header: 'Confirmation',
                    message: (
                        <div className="is-flex">
                            ⚠️ <p>Revenir en arrière effacera tout ce qui a été saisi jusqu'à maintenant.</p>
                        </div>
                    ),
                    defaultFocus: 'reject',
                    acceptClassName: 'button is-warning',
                    rejectClassName: 'button is-secondary mr-2',
                    accept
                });
            } else {
                setActiveIndex(activeIndex - 1);
                setIsValid(true);
            }
        };

        return (
            <div className="container">
                <Toast ref={toast} position="bottom-right" />

                <div className="columns is-vcentered is-multiline">
                    <div className="column is-full has-text-centered">
                        <p className="title is-2"><strong>Votre entreprise</strong></p>
                        <Stepper activeStep={activeIndex} alternativeLabel>
                            {items.map((item, index) => (
                                <Step key={index}>
                                    <StepLabel>{item.label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    <div className="column mt-6 mx-6 px-6">
                        {stepsComponents[activeIndex]}
                    </div>
                </div>

                <ConfirmPopup />
                <div className="buttons is-justify-content-space-between  mx-6 px-6">
                    <button
                        className="button is-info is-outlined is-light is-large is-responsive mt-6 mb-6"
                        disabled={submitLoading}
                        onClick={handleBackButton}
                    >
                        Retour
                    </button>
                    {activeIndex !== 3 && (
                        <button
                            className="button is-info is-light is-large is-responsive mt-6 mb-6"
                            disabled={!isValid}
                            onClick={() => { setActiveIndex(activeIndex + 1); }}
                        >
                            Continuer
                        </button>
                    )}
                    {activeIndex === 3 && (
                        <button
                            className={clsx('button is-primary is-light is-outlined is-large is-responsive mt-6 mb-6', {
                                'is-loading': submitLoading
                            })}
                            onClick={handleSubmit}
                        >
                            Sauvegarder et créer
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <section className='home'>
            {renderChoice()}
        </section>
    );
};

export default CompanyCreate;